import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getProject, updateProject, addProjectComment,
  uploadAttachment, deleteAttachment, getAttachmentFileUrl,
  getAssessment,
  getProjectReports, addProjectReport,
} from '../services/projectService';
import FootprintPickerMap from '../components/land/FootprintPickerMap';

// ─── Shared style tokens ──────────────────────────────────────────────────────
const S = {
  page:  { padding: '28px 32px', maxWidth: 1060, margin: '0 auto', color: '#e2e8f0' },
  inp:   {
    background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
    color: '#e2e8f0', padding: '8px 11px', fontSize: 13,
    width: '100%', boxSizing: 'border-box',
  },
  label: { color: '#94a3b8', fontSize: 11, marginBottom: 4, display: 'block' },
  tab:   (a) => ({
    background: a ? '#1e3a5f' : 'none', border: `1px solid ${a ? '#3b82f6' : '#334155'}`,
    color: a ? '#7dd3fc' : '#64748b', borderRadius: 6, padding: '6px 14px',
    cursor: 'pointer', fontSize: 12, fontWeight: a ? 600 : 400, whiteSpace: 'nowrap',
  }),
  btn:   (bg = '#1d4ed8') => ({
    background: bg, border: 'none', borderRadius: 6, color: '#fff',
    padding: '7px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  }),
  btnSm: (bg = '#1e293b', border = '#334155') => ({
    background: bg, border: `1px solid ${border}`, borderRadius: 5,
    color: '#94a3b8', padding: '4px 10px', cursor: 'pointer', fontSize: 11,
  }),
};

const STATUS_COLOR = {
  draft: '#64748b', active: '#22c55e', closed: '#3b82f6', archived: '#78716c',
};
const TYPE_COLOR = { public: '#0ea5e9', community: '#a855f7', hybrid: '#f59e0b' };
const CONCERN_COLOR = { none: '#64748b', low: '#22c55e', medium: '#f59e0b', high: '#ef4444' };
const ATTACH_CATS = ['all', 'permit', 'document', 'photo', 'other'];

function Badge({ label, color }) {
  return (
    <span style={{
      background: '#0f172a', border: `1px solid ${color || '#334155'}`,
      color: color || '#94a3b8', borderRadius: 5, padding: '3px 9px', fontSize: 11, fontWeight: 600,
    }}>{label}</span>
  );
}

function AttachIcon({ mimetype }) {
  if (!mimetype) return <>&#128203;</>;
  if (mimetype.startsWith('image/')) return <>&#128444;</>;
  if (mimetype === 'application/pdf') return <>&#128196;</>;
  if (mimetype.includes('zip')) return <>&#128230;</>;
  if (mimetype.includes('json')) return <>&#128506;</>;
  return <>&#128203;</>;
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────
function OverviewTab({ project, canEdit, onUpdated }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [editForm, setEditForm]   = useState({});

  const startEdit = () => {
    setEditForm({
      title:         project.title || '',
      description:   project.description || '',
      proponentOrg:  project.proponentOrg || '',
      projectNumber: project.projectNumber || '',
      address:       project.address || '',
      type:          project.type || 'public',
      status:        project.status || 'draft',
      startDate:     project.startDate ? new Date(project.startDate).toISOString().slice(0, 10) : '',
      endDate:       project.endDate   ? new Date(project.endDate).toISOString().slice(0, 10)   : '',
      contactName:   project.contactName  || '',
      contactPhone:  project.contactPhone || '',
      contactEmail:  project.contactEmail || '',
    });
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await updateProject(project._id, {
        ...editForm,
        startDate: editForm.startDate || null,
        endDate:   editForm.endDate   || null,
      });
      onUpdated(res.data);
      setIsEditing(false);
    } catch (err) {
      alert(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (d) =>
    d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const community = project.tenantId?.branding?.displayName || project.tenantId?.name;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          {community && (
            <span style={{
              background: '#0f172a', border: '1px solid #334155', borderRadius: 5,
              padding: '2px 8px', fontSize: 11, color: '#64748b', marginBottom: 8, display: 'inline-block',
            }}>{community}</span>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <Badge label={project.type}   color={TYPE_COLOR[project.type]} />
            <Badge label={project.status} color={STATUS_COLOR[project.status]} />
          </div>
        </div>
        {canEdit && !isEditing && (
          <button onClick={startEdit} style={S.btnSm()}>&#9999; Edit</button>
        )}
      </div>

      {!isEditing ? (
        <div>
          {(project.projectNumber || project.proponentOrg) && (
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              {project.projectNumber && <span>Ref: {project.projectNumber}</span>}
              {project.projectNumber && project.proponentOrg && <span> &middot; </span>}
              {project.proponentOrg && <span>{project.proponentOrg}</span>}
            </div>
          )}
          {project.address && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
              &#128205; {project.address}
            </div>
          )}
          <p style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.6, marginBottom: 16 }}>
            {project.description || <em style={{ color: '#475569' }}>No description provided.</em>}
          </p>
          <div style={{ display: 'flex', gap: 20, fontSize: 12, color: '#64748b' }}>
            <span>Start: {fmt(project.startDate)}</span>
            <span>End: {fmt(project.endDate)}</span>
            <span>Submitted: {fmt(project.createdAt)}</span>
          </div>
          {(project.contactName || project.contactPhone || project.contactEmail) && (
            <div style={{
              marginTop: 16, padding: '12px 14px',
              background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8,
              fontSize: 12, color: '#94a3b8',
            }}>
              <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 6 }}>PROJECT CONTACT</div>
              {project.contactName  && <div>{project.contactName}</div>}
              {project.contactPhone && <div>{project.contactPhone}</div>}
              {project.contactEmail && (
                <a href={`mailto:${project.contactEmail}`} style={{ color: '#7dd3fc' }}>
                  {project.contactEmail}
                </a>
              )}
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSave} style={{ display: 'grid', gap: 12, maxWidth: 700 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 200px' }}>
              <span style={S.label}>TITLE</span>
              <input style={S.inp} value={editForm.title}
                onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} required />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <span style={S.label}>PROJECT NUMBER</span>
              <input style={S.inp} value={editForm.projectNumber}
                onChange={e => setEditForm(p => ({ ...p, projectNumber: e.target.value }))} />
            </div>
          </div>
          <div>
            <span style={S.label}>PROPONENT ORG</span>
            <input style={S.inp} value={editForm.proponentOrg}
              onChange={e => setEditForm(p => ({ ...p, proponentOrg: e.target.value }))} />
          </div>
          <div>
            <span style={S.label}>ADDRESS</span>
            <input style={S.inp} value={editForm.address}
              onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} />
          </div>
          <div>
            <span style={S.label}>DESCRIPTION</span>
            <textarea style={{ ...S.inp, resize: 'vertical', minHeight: 72 }} value={editForm.description}
              onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 120px' }}>
              <span style={S.label}>TYPE</span>
              <select style={S.inp} value={editForm.type}
                onChange={e => setEditForm(p => ({ ...p, type: e.target.value }))}>
                {['public', 'community', 'hybrid'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <span style={S.label}>STATUS</span>
              <select style={S.inp} value={editForm.status}
                onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
                {['draft', 'active', 'closed', 'archived'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 130px' }}>
              <span style={S.label}>START DATE</span>
              <input style={S.inp} type="date" value={editForm.startDate}
                onChange={e => setEditForm(p => ({ ...p, startDate: e.target.value }))} />
            </div>
            <div style={{ flex: '1 1 130px' }}>
              <span style={S.label}>END DATE</span>
              <input style={S.inp} type="date" value={editForm.endDate}
                onChange={e => setEditForm(p => ({ ...p, endDate: e.target.value }))} />
            </div>
          </div>
          <div style={{
            borderTop: '1px solid #334155', paddingTop: 12, marginTop: 4,
            display: 'flex', gap: 10, flexWrap: 'wrap',
          }}>
            <div style={{ flex: '1 1 160px' }}>
              <span style={S.label}>CONTACT NAME</span>
              <input style={S.inp} value={editForm.contactName}
                onChange={e => setEditForm(p => ({ ...p, contactName: e.target.value }))}
                placeholder="Jane Smith" />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <span style={S.label}>CONTACT PHONE</span>
              <input style={S.inp} type="tel" value={editForm.contactPhone}
                onChange={e => setEditForm(p => ({ ...p, contactPhone: e.target.value }))}
                placeholder="+1 (403) 555-0123" />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <span style={S.label}>CONTACT EMAIL</span>
              <input style={S.inp} type="email" value={editForm.contactEmail}
                onChange={e => setEditForm(p => ({ ...p, contactEmail: e.target.value }))}
                placeholder="contact@example.com" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" disabled={saving} style={S.btn()}>
              {saving ? 'Saving\u2026' : 'Save Changes'}
            </button>
            <button type="button" onClick={() => setIsEditing(false)} style={S.btnSm()}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div style={{ marginTop: 32 }}>
        <CommentsSection projectId={project._id} comments={project.comments || []} />
      </div>
    </div>
  );
}

// ─── Comments ─────────────────────────────────────────────────────────────────
function CommentsSection({ projectId, comments }) {
  const { user } = useAuth();
  const [text, setText]       = useState('');
  const [posting, setPosting] = useState(false);
  const [list, setList]       = useState(comments);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const res = await addProjectComment(projectId, text.trim());
      setList(prev => [...prev, res.data.comment]);
      setText('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Post failed');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>
        Comments ({list.length})
      </div>
      {list.length === 0 && (
        <div style={{ color: '#475569', fontSize: 12, marginBottom: 12 }}>No comments yet.</div>
      )}
      <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
        {list.map((c, i) => {
          const author = c.userId
            ? `${c.userId.firstName || ''} ${c.userId.lastName || ''}`.trim() || c.userId.email || 'User'
            : 'User';
          return (
            <div key={c._id || i} style={{
              background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, padding: '10px 12px',
            }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#94a3b8' }}>{author}</div>
              <div style={{ marginTop: 4, fontSize: 13, color: '#cbd5e1' }}>{c.content}</div>
              <div style={{ marginTop: 4, fontSize: 11, color: '#475569' }}>
                {c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}
              </div>
            </div>
          );
        })}
      </div>
      {user && (
        <form onSubmit={handlePost} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={2}
            placeholder="Add a comment\u2026"
            style={{ ...S.inp, flex: '1 1 300px', resize: 'vertical' }}
          />
          <button type="submit" disabled={posting || !text.trim()} style={S.btn()}>
            {posting ? 'Posting\u2026' : 'Post'}
          </button>
        </form>
      )}
    </div>
  );
}

// ─── Tab: Location ────────────────────────────────────────────────────────────
function LocationTab({ project }) {
  if (!project.geometry) {
    return (
      <div style={{
        background: '#1e293b', border: '1px dashed #334155', borderRadius: 10,
        padding: '36px 20px', textAlign: 'center',
      }}>
        <div style={{ fontSize: 32, marginBottom: 10 }}>&#128506;</div>
        <div style={{ color: '#64748b', fontSize: 14 }}>
          No location footprint defined for this project.
        </div>
      </div>
    );
  }
  return (
    <div>
      {project.address && (
        <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 14 }}>
          &#128205; {project.address}
        </div>
      )}
      <div style={{ fontSize: 11, color: '#475569', marginBottom: 10 }}>
        Coordinate system: {project.crs || 'EPSG:4326 (WGS84)'}
      </div>
      <FootprintPickerMap
        mode="view"
        uploadedGeometry={project.geometry}
        readOnly
        height="420px"
      />
    </div>
  );
}

// ─── Tab: Attachments ─────────────────────────────────────────────────────────
function AttachmentsTab({ project, canUpload }) {
  const [catFilter, setCatFilter]   = useState('all');
  const [uploading, setUploading]   = useState(false);
  const [uploadForm, setUploadForm] = useState({ category: 'document', description: '' });
  const [showUpload, setShowUpload] = useState(false);
  const [attachErr, setAttachErr]   = useState('');
  const fileRef = useRef(null);
  const [attachments, setAttachments] = useState(project.attachments || []);

  const filtered = catFilter === 'all'
    ? attachments
    : attachments.filter(a => a.category === catFilter);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files[0];
    if (!file) { setAttachErr('Please choose a file.'); return; }
    setAttachErr('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', uploadForm.category);
      fd.append('description', uploadForm.description);
      const res = await uploadAttachment(project._id, fd);
      setAttachments(prev => [...prev, res.data.attachment]);
      setShowUpload(false);
      if (fileRef.current) fileRef.current.value = '';
      setUploadForm({ category: 'document', description: '' });
    } catch (err) {
      setAttachErr(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await deleteAttachment(project._id, attachId);
      setAttachments(prev => prev.filter(a => a._id !== attachId));
    } catch (err) {
      alert(err?.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div>
      {canUpload && (
        <div style={{ marginBottom: 18 }}>
          {!showUpload ? (
            <button onClick={() => setShowUpload(true)} style={S.btn('#1e293b')}>
              + Upload Attachment
            </button>
          ) : (
            <form onSubmit={handleUpload} style={{
              background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>
                Upload Attachment
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ flex: '1 1 200px' }}>
                  <span style={S.label}>FILE *</span>
                  <input ref={fileRef} type="file" style={{ ...S.inp, padding: '5px' }}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.json,.geojson,.zip" />
                </div>
                <div style={{ flex: '0 0 150px' }}>
                  <span style={S.label}>CATEGORY</span>
                  <select style={S.inp} value={uploadForm.category}
                    onChange={e => setUploadForm(p => ({ ...p, category: e.target.value }))}>
                    {['permit', 'document', 'photo', 'other'].map(c => (
                      <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <span style={S.label}>DESCRIPTION</span>
                <input style={S.inp} value={uploadForm.description}
                  onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Optional note about this file" />
              </div>
              {attachErr && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 8 }}>{attachErr}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={uploading} style={S.btn()}>
                  {uploading ? 'Uploading\u2026' : 'Upload'}
                </button>
                <button type="button" onClick={() => { setShowUpload(false); setAttachErr(''); }}
                  style={S.btnSm()}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {ATTACH_CATS.map(c => (
          <button key={c} style={S.tab(catFilter === c)} onClick={() => setCatFilter(c)}>
            {c.charAt(0).toUpperCase() + c.slice(1)}
            {c !== 'all' && (
              <span style={{ marginLeft: 4, opacity: 0.7 }}>
                ({attachments.filter(a => a.category === c).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: '#475569', fontSize: 13, padding: '20px 0' }}>
          No {catFilter === 'all' ? '' : catFilter + ' '}attachments yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {filtered.map(a => {
            const uploader = a.uploadedBy
              ? `${a.uploadedBy.firstName || ''} ${a.uploadedBy.lastName || ''}`.trim() || a.uploadedBy.email
              : '';
            const date = a.uploadedAt ? new Date(a.uploadedAt).toLocaleDateString() : '';
            const fileUrl = getAttachmentFileUrl(project._id, a._id);
            const downloadUrl = getAttachmentFileUrl(project._id, a._id, { download: true });
            return (
              <div key={a._id} style={{
                background: '#1e293b', border: '1px solid #334155', borderRadius: 8,
                padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>
                  <AttachIcon mimetype={a.mimetype} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 600, fontSize: 13, color: '#f1f5f9',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {a.originalName}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Badge label={a.category} color={
                      { permit: '#f59e0b', document: '#3b82f6', photo: '#a855f7', other: '#64748b' }[a.category]
                    } />
                    {a.description && (
                      <span style={{ fontSize: 11, color: '#64748b' }}>{a.description}</span>
                    )}
                    {a.size && <span style={{ fontSize: 11, color: '#475569' }}>{fmtSize(a.size)}</span>}
                    {uploader && <span style={{ fontSize: 11, color: '#475569' }}>by {uploader}</span>}
                    {date && <span style={{ fontSize: 11, color: '#475569' }}>{date}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <a href={fileUrl} target="_blank" rel="noreferrer" style={{
                    background: '#0f172a', border: '1px solid #334155', borderRadius: 5,
                    color: '#cbd5e1', padding: '4px 10px', fontSize: 11, textDecoration: 'none',
                  }}>Open</a>
                  <a href={downloadUrl} download={a.originalName} style={{
                    background: '#0f172a', border: '1px solid #334155', borderRadius: 5,
                    color: '#7dd3fc', padding: '4px 10px', fontSize: 11, textDecoration: 'none',
                  }}>&#8595; Download</a>
                  {canUpload && (
                    <button onClick={() => handleDelete(a._id)} style={S.btnSm('#0f172a', '#ef4444')}>
                      <span style={{ color: '#f87171' }}>&#10005;</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Assessment ──────────────────────────────────────────────────────────
function AssessmentTab({ project, isProponent }) {
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');

  if (!project.geometry) {
    return (
      <div style={{
        background: '#1e293b', border: '1px dashed #334155', borderRadius: 10,
        padding: '36px 20px', textAlign: 'center',
      }}>
        <div style={{ fontSize: 32, marginBottom: 10 }}>&#128269;</div>
        <div style={{ color: '#64748b', fontSize: 14 }}>
          Define a location footprint on the Location tab before running an assessment.
        </div>
      </div>
    );
  }

  const run = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await getAssessment(project._id);
      setResult(res.data);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Assessment failed');
    } finally {
      setLoading(false);
    }
  };

  function ConcernCard({ icon, title, concern, detail }) {
    return (
      <div style={{
        background: '#1e293b', border: `1px solid ${CONCERN_COLOR[concern] || '#334155'}`,
        borderRadius: 10, padding: '16px 18px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 20 }}>{icon}</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#f1f5f9' }}>{title}</span>
          <span style={{
            marginLeft: 'auto', background: '#0f172a',
            border: `1px solid ${CONCERN_COLOR[concern] || '#334155'}`,
            color: CONCERN_COLOR[concern] || '#94a3b8',
            borderRadius: 5, padding: '2px 9px', fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase',
          }}>{concern} concern</span>
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>{detail}</div>
      </div>
    );
  }

  return (
    <div>
      {isProponent && (
        <div style={{
          background: '#1c1917', border: '1px solid #78716c', borderRadius: 8,
          padding: '10px 14px', fontSize: 12, color: '#a8a29e', marginBottom: 16,
        }}>
          &#128274; Exact coordinates of sensitive cultural and ecological locations are
          protected. Results show concern levels only.
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={run} disabled={loading} style={S.btn()}>
          {loading ? 'Running\u2026' : '\u25b6 Run Assessment'}
        </button>
        {result && (
          <span style={{ fontSize: 11, color: '#64748b' }}>
            Last assessed: {new Date(result.assessedAt).toLocaleString()}
          </span>
        )}
      </div>

      {err && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 14 }}>{err}</div>}

      {result && (
        <div style={{ display: 'grid', gap: 14 }}>
          <ConcernCard
            icon="&#127807;" title="Harvesting Activity"
            concern={result.harvesting.concern}
            detail={
              result.harvesting.observationCount === 0
                ? 'No harvesting observations found in the project area.'
                : `${result.harvesting.observationCount} observation(s) across ` +
                  `${result.harvesting.factors.length} factor type(s) in intersecting land units.`
            }
          />
          <ConcernCard
            icon="&#128506;" title="Land Units"
            concern={result.landUnits.concern}
            detail={
              result.landUnits.count === 0
                ? 'No managed land units intersect the project footprint.'
                : `${result.landUnits.count} land unit(s) intersect the footprint: ` +
                  result.landUnits.units.map(u => u.name).join(', ')
            }
          />
          <ConcernCard
            icon="&#128176;" title="Land Values"
            concern={result.landValues.concern}
            detail={
              result.landValues.count === 0
                ? 'No land value records intersect the project footprint.'
                : `${result.landValues.count} land value record(s) found in the project area.`
            }
          />
        </div>
      )}
    </div>
  );
}

// ─── Tab: Community Reports ───────────────────────────────────────────────────
const CAT_LABEL = {
  concern: 'Concern', question: 'Question', support: 'Support',
  information_request: 'Info Request',
};
const CAT_COLOR = {
  concern: '#ef4444', question: '#3b82f6', support: '#22c55e', information_request: '#f59e0b',
};

function CommunityReportsTab({ project, user }) {
  const { memberships } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [reportForm, setReportForm]     = useState({ category: 'concern', description: '', tenantId: '' });
  const [showReportForm, setShowReportForm] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [reportErr, setReportErr]       = useState('');

  const activeMemberships = (memberships || []).filter(m => m.status === 'active' && m.tenant);

  const canReport      = ['active', 'closed'].includes(project.status);
  const isOwner        = project.createdBy && user &&
    project.createdBy.toString() === user._id?.toString();
  const isStaff        = ['cip_admin', 'cip_staff'].includes(user?.globalRole);
  const canViewReports = isOwner || isStaff;

  useEffect(() => {
    if (!canViewReports) return;
    setLoading(true);
    getProjectReports(project._id)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || 'Failed to load reports'))
      .finally(() => setLoading(false));
  }, [project._id, canViewReports]);

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!reportForm.description.trim()) { setReportErr('Description required.'); return; }
    setSubmitting(true);
    setReportErr('');
    try {
      await addProjectReport(project._id, {
        category:    reportForm.category,
        description: reportForm.description.trim(),
        tenantId:    reportForm.tenantId || undefined,
      });
      setShowReportForm(false);
      setReportForm({ category: 'concern', description: '', tenantId: '' });
      alert('Report submitted. Thank you.');
    } catch (e2) {
      setReportErr(e2?.response?.data?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canReport && !canViewReports) {
    return (
      <div style={{ color: '#64748b', fontSize: 13 }}>
        Community reports are available for active or closed projects.
      </div>
    );
  }

  return (
    <div>
      {canReport && !isOwner && !isStaff && (
        <div style={{ marginBottom: 20 }}>
          {!showReportForm ? (
            <button onClick={() => setShowReportForm(true)} style={S.btn('#0c4a6e')}>
              + Submit a Report / Flag
            </button>
          ) : (
            <form onSubmit={handleSubmitReport} style={{
              background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>
                Submit a Report
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 160px' }}>
                  <span style={S.label}>CATEGORY</span>
                  <select style={S.inp} value={reportForm.category}
                    onChange={e => setReportForm(p => ({ ...p, category: e.target.value }))}>
                    {Object.entries(CAT_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: '1 1 160px' }}>
                  <span style={S.label}>SUBMITTING AS</span>
                  <select style={S.inp} value={reportForm.tenantId}
                    onChange={e => setReportForm(p => ({ ...p, tenantId: e.target.value }))}>
                    <option value="">General Public</option>
                    {activeMemberships.map(m => (
                      <option key={m._id} value={m.tenant._id}>
                        {m.tenant.branding?.displayName || m.tenant.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <span style={S.label}>DESCRIPTION *</span>
                <textarea style={{ ...S.inp, resize: 'vertical', minHeight: 80 }}
                  value={reportForm.description}
                  onChange={e => setReportForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe your concern, question, or feedback." />
              </div>
              {reportErr && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 8 }}>{reportErr}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={submitting} style={S.btn()}>
                  {submitting ? 'Submitting\u2026' : 'Submit'}
                </button>
                <button type="button" onClick={() => { setShowReportForm(false); setReportErr(''); }}
                  style={S.btnSm()}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      {canViewReports && (
        <div>
          {loading && <div style={{ color: '#64748b', fontSize: 13 }}>Loading reports\u2026</div>}
          {err && <div style={{ color: '#f87171', fontSize: 13 }}>{err}</div>}
          {data && data.total === 0 && (
            <div style={{ color: '#475569', fontSize: 13 }}>No community reports yet.</div>
          )}
          {data && data.groups.map(group => (
            <div key={group.tenantId || '__genpop__'} style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 8,
                borderBottom: '1px solid #1e293b', paddingBottom: 6,
              }}>
                {group.tenantName}
                <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 11 }}>
                  ({group.reports.length} report{group.reports.length !== 1 ? 's' : ''})
                </span>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {group.reports.map((r, i) => (
                  <div key={r.id || i} style={{
                    background: '#1e293b', border: '1px solid #334155',
                    borderLeft: `3px solid ${CAT_COLOR[r.category] || '#334155'}`,
                    borderRadius: 8, padding: '10px 14px',
                  }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                      <Badge label={CAT_LABEL[r.category] || r.category} color={CAT_COLOR[r.category]} />
                      <Badge label={r.status}
                        color={r.status === 'open' ? '#f59e0b' : r.status === 'resolved' ? '#22c55e' : '#3b82f6'} />
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#475569' }}>
                        {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#cbd5e1' }}>{r.description}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ProjectPage (root) ───────────────────────────────────────────────────────
const TABS = [
  { key: 'overview',    label: 'Overview' },
  { key: 'location',   label: '\uD83D\uDCCD Location' },
  { key: 'attachments',label: '\uD83D\uDCCE Attachments' },
  { key: 'assessment', label: '\uD83D\uDD0D Assessment' },
  { key: 'reports',    label: '\uD83D\uDCE3 Community Reports' },
];

export default function ProjectPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const canEdit     = ['cip_admin', 'cip_staff'].includes(user?.globalRole);
  const isOwner     = project && user &&
    project.createdBy?.toString() === user._id?.toString();
  const isProponent = !canEdit && (user?.globalRole === 'proponent' || isOwner);
  const canUpload   = canEdit || isOwner;

  useEffect(() => {
    setLoading(true);
    getProject(id)
      .then(r => setProject(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ padding: 40, color: '#64748b' }}>Loading\u2026</div>;
  if (!project) return <div style={{ padding: 40, color: '#f87171' }}>Project not found.</div>;

  return (
    <div style={S.page}>
      <div style={{ marginBottom: 20 }}>
        <Link to="/proponent" style={{ fontSize: 12, color: '#64748b', textDecoration: 'none' }}>
          \u2190 Proponent Portal
        </Link>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', margin: '6px 0 0' }}>
          {project.title}
        </h2>
      </div>

      <div style={{
        display: 'flex', gap: 6, marginBottom: 22, flexWrap: 'wrap',
        borderBottom: '1px solid #1e293b', paddingBottom: 12,
      }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={S.tab(activeTab === t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <OverviewTab project={project} canEdit={canEdit} onUpdated={setProject} />
      )}
      {activeTab === 'location' && <LocationTab project={project} />}
      {activeTab === 'attachments' && (
        <AttachmentsTab project={project} canUpload={canUpload} />
      )}
      {activeTab === 'assessment' && (
        <AssessmentTab project={project} isProponent={isProponent} />
      )}
      {activeTab === 'reports' && (
        <CommunityReportsTab project={project} user={user} />
      )}
    </div>
  );
}
